import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Save, Trash2, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Manager-only — admin-managed stock categories (Material, Consumable, PPE,
 * Tool, plus any quick-added ones like Pads/Gloves/Sanitiser). Uses the
 * generic admin_list factory on the backend (`/stock-categories`) which
 * cascades renames to `db.stock.category`. Renaming to an EXISTING category
 * (case-insensitive) auto-merges — that's how you fix duplicates like
 * "Consumables" → "Consumable".
 */
const ManageStockCategoriesDialog = ({ open, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/stock-categories");
      setRows(data || []);
    } catch (_) {
      toast.error("Could not load categories");
    }
  };
  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const duplicate = (() => {
    const n = newName.trim().toLowerCase();
    if (!n) return null;
    return rows.find((r) => r.name.toLowerCase() === n) || null;
  })();

  const create = async () => {
    const name = newName.trim();
    if (!name || duplicate) return;
    setBusy(true);
    try {
      await api.post("/stock-categories", { name });
      setNewName("");
      await load();
      onChanged?.();
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const save = async (row) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/stock-categories/${row.id}`, {
        name: row.name,
        sort: row.sort,
      });
      // If the backend returned a DIFFERENT id than we sent, it merged us
      // into an existing category — surface that clearly.
      if (data?.id && data.id !== row.id) {
        toast.success(`Merged into "${data.name}" — stock retagged`);
      } else {
        toast.success("Saved");
      }
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete category "${row.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/stock-categories/${row.id}`);
      await load();
      onChanged?.();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const move = (idx, dir) => {
    const newRows = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= newRows.length) return;
    [newRows[idx], newRows[j]] = [newRows[j], newRows[idx]];
    newRows.forEach((r, i) => (r.sort = i));
    setRows(newRows);
    api.put(`/stock-categories/${newRows[idx].id}`, { sort: idx }).catch(() => {});
    api.put(`/stock-categories/${newRows[j].id}`, { sort: j }).then(onChanged).catch(() => {});
  };

  const update = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="manage-stock-categories-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(640px,94vw)] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Inventory</div>
            <h3 className="font-display text-2xl">Manage categories</h3>
          </div>
          <button
            data-testid="manage-stock-categories-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          {rows.length === 0 && (
            <div className="text-sm text-[#607087] italic text-center py-4">
              No categories yet.
            </div>
          )}
          {rows.map((row, idx) => (
            <div
              key={row.id}
              data-testid={`stock-cat-row-${row.name}`}
              className="border-2 border-black rounded-xl p-3 flex items-center gap-2"
            >
              <div className="flex gap-1">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="nb-btn bg-white p-1.5 disabled:opacity-30"
                  aria-label="up"
                >
                  <ArrowUp size={12} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === rows.length - 1}
                  className="nb-btn bg-white p-1.5 disabled:opacity-30"
                  aria-label="down"
                >
                  <ArrowDown size={12} strokeWidth={2.5} />
                </button>
              </div>
              <input
                data-testid={`stock-cat-name-${row.name}`}
                className="nb-input flex-1"
                value={row.name}
                onChange={(e) => update(row.id, { name: e.target.value })}
              />
              <button
                data-testid={`stock-cat-save-${row.name}`}
                onClick={() => save(row)}
                disabled={busy}
                className="nb-btn nb-btn-primary px-3 py-2 text-sm flex items-center gap-1"
              >
                <Save size={14} strokeWidth={2.5} /> Save
              </button>
              <button
                data-testid={`stock-cat-delete-${row.name}`}
                onClick={() => remove(row)}
                disabled={busy}
                className="nb-btn nb-btn-danger p-2"
              >
                <Trash2 size={14} strokeWidth={2.5} />
              </button>
            </div>
          ))}

          <div className="border-2 border-dashed border-[#001740] rounded-xl p-3 flex items-end gap-3 bg-[#F4F8FB]">
            <label className="flex-1">
              <div className="label-eyebrow mb-1">New category</div>
              <input
                data-testid="new-stock-cat-name"
                className="nb-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !duplicate && create()}
                placeholder="e.g. Pads, Sanitiser, Gloves…"
              />
            </label>
            <button
              data-testid="new-stock-cat-add"
              onClick={create}
              disabled={busy || !newName.trim() || !!duplicate}
              className="nb-btn nb-btn-success px-4 py-2 text-sm flex items-center gap-1"
            >
              <Plus size={14} strokeWidth={2.5} /> Add
            </button>
          </div>

          {duplicate && (
            <div
              data-testid="stock-cat-duplicate-warning"
              className="text-xs font-semibold px-3 py-2 rounded-md border-2 border-[#C7A43B] bg-[#FFF7DB] text-[#5B4500]"
            >
              {`"${duplicate.name}" already exists — edit its row above instead.`}
            </div>
          )}

          <div className="label-eyebrow text-[#607087] mt-1">
            Renaming a category cascades to existing items. Renaming to a name
            that already exists <b>merges</b> the two (all items retagged).
            Delete is blocked while items still reference it.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageStockCategoriesDialog;
